<template>
  <div class="pay-verify-container">
    <div class="verify-card">
      <!-- 防诈骗提示 -->
      <div class="anti-fraud-notice">
        <div class="notice-header">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L3 7V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>{{ $t('payVerify.antiFraud.title') }}</span>
        </div>
        <ul class="notice-list">
          <li><strong class="warning-text">{{ $t('payVerify.antiFraud.tips.official') }}：</strong>{{ $t('payVerify.antiFraud.tips.officialDesc') }}</li>
          <li><strong class="warning-text">{{ $t('payVerify.antiFraud.tips.fraud') }}：</strong>{{ $t('payVerify.antiFraud.tips.fraudDesc') }}</li>
          <li><strong class="warning-text">{{ $t('payVerify.antiFraud.tips.verify') }}：</strong>{{ $t('payVerify.antiFraud.tips.verifyDesc') }}</li>
          <li><strong class="warning-text">{{ $t('payVerify.antiFraud.tips.contact') }}：</strong>{{ $t('payVerify.antiFraud.tips.contactDesc') }}</li>
        </ul>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <p>{{ $t('payVerify.status.loading') }}</p>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="error-container">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 16.9706V12Z" stroke="#EF4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p class="error-text">{{ error }}</p>
        <button class="btn btn-secondary" @click="retryFetch">{{ $t('payVerify.actions.retry') }}</button>
      </div>

      <!-- 订单信息 -->
      <div v-else-if="orderInfo" class="order-info">
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">{{ $t('payVerify.orderInfo.orderNo') }}</span>
            <span class="info-value order-no" @click="copyText(orderInfo.outTradeNo)">
              {{ orderInfo.outTradeNo }}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="copy-icon">
                <path d="M8 4V16C8 17.1046 8.89543 18 10 18H18C19.1046 18 20 17.1046 20 16V7.24162C20 6.7034 19.7831 6.18789 19.3982 5.81161L16.6018 3.06999C16.2175 2.69371 15.7064 2.5 15.1754 2.5H10C8.89543 2.5 8 3.39543 8 4.5V4Z" stroke="currentColor" stroke-width="2"/>
                <path d="M16 18V20C16 21.1046 15.1046 22 14 22H6C4.89543 22 4 21.1046 4 20V9C4 7.89543 4.89543 7 6 7H8" stroke="currentColor" stroke-width="2"/>
              </svg>
            </span>
          </div>

          <div class="info-row highlight">
            <span class="info-label">{{ $t('payVerify.orderInfo.rechargeAmount') }}</span>
            <span class="info-value amount">{{ orderInfo.originalAmount }} {{ $t('payVerify.orderInfo.coins') }}</span>
          </div>

          <div class="info-row highlight">
            <span class="info-label">{{ $t('payVerify.orderInfo.actualPayment') }}</span>
            <span class="info-value amount-pay">¥{{ orderInfo.money }}</span>
          </div>
        </div>

        <!-- 确认按钮 -->
        <div class="action-section">
          <button
            class="btn btn-primary btn-large"
            @click="confirmAndPay"
            :disabled="redirecting"
          >
            <span v-if="redirecting" class="btn-spinner"></span>
            {{ redirecting ? $t('payVerify.actions.redirecting') : $t('payVerify.actions.confirm') }}
          </button>

          <p class="action-tip">{{ $t('payVerify.actions.tip') }}</p>
        </div>
      </div>

      <!-- 客服联系方式 -->
      <div class="contact-section">
        <p class="contact-text">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ $t('payVerify.contact.text') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { getEasyPayOrderInfo } from '@/api/sms'

export default {
  name: 'PayVerify',
  data() {
    return {
      loading: true,
      error: null,
      orderInfo: null,
      redirecting: false
    }
  },
  mounted() {
    this.fetchOrderInfo()
  },
  methods: {
    async fetchOrderInfo() {
      const outTradeNo = this.$route.query.outTradeNo

      if (!outTradeNo) {
        this.error = this.$t('payVerify.status.missingParam')
        this.loading = false
        return
      }

      this.loading = true
      this.error = null

      try {
        const response = await getEasyPayOrderInfo(outTradeNo)

        if (response.status === 200 || response.success) {
          this.orderInfo = response.data
        } else {
          this.error = response.msg || this.$t('payVerify.status.error')
        }
      } catch (error) {
        console.error('获取订单信息失败:', error)
        this.error = error.response?.data?.msg || this.$t('payVerify.status.networkError')
      } finally {
        this.loading = false
      }
    },
    retryFetch() {
      this.fetchOrderInfo()
    },
    async copyText(text) {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        ElMessage.success(this.$t('messages.copySuccess'))
      } catch (err) {
        ElMessage.error(this.$t('messages.copyError'))
      }
    },
    confirmAndPay() {
      if (!this.orderInfo || !this.orderInfo.payUrl) {
        ElMessage.error(this.$t('payVerify.status.invalidLink'))
        return
      }

      this.redirecting = true

      // 跳转到真实支付地址
      window.location.href = this.orderInfo.payUrl
    }
  }
}
</script>

<style scoped>
.pay-verify-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.verify-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 100%;
  overflow: hidden;
}

/* 防诈骗提示 */
.anti-fraud-notice {
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  border: 2px solid #F59E0B;
  border-radius: 12px;
  padding: 20px;
  margin: 24px;
}

.notice-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 700;
  color: #92400E;
}

.notice-header svg {
  color: #D97706;
  flex-shrink: 0;
}

.notice-list {
  margin: 0;
  padding-left: 20px;
  list-style: none;
}

.notice-list li {
  position: relative;
  padding-left: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 1.6;
  color: #78350F;
}

.notice-list li:last-child {
  margin-bottom: 0;
}

.notice-list li::before {
  content: '•';
  position: absolute;
  left: -12px;
  color: #F59E0B;
  font-weight: bold;
  font-size: 18px;
}

.warning-text {
  color: #DC2626;
  font-weight: 600;
}

/* 加载状态 */
.loading-container {
  padding: 60px 24px;
  text-align: center;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #E5E7EB;
  border-top: 4px solid #4461F2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-container p {
  color: #6B7280;
  font-size: 14px;
}

/* 错误状态 */
.error-container {
  padding: 60px 24px;
  text-align: center;
}

.error-container svg {
  margin-bottom: 16px;
}

.error-text {
  color: #DC2626;
  font-size: 16px;
  margin-bottom: 24px;
}

/* 订单信息 */
.order-info {
  padding: 0 24px 24px;
}

.info-section {
  background: #F9FAFB;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #E5E7EB;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row.highlight {
  background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
  border-radius: 8px;
  padding: 16px;
  margin-top: 12px;
  border: 2px solid #93C5FD;
}

.info-label {
  font-size: 14px;
  color: #6B7280;
  font-weight: 500;
}

.info-value {
  font-size: 14px;
  color: #111827;
  font-weight: 600;
}

.order-no {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #4461F2;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s;
}

.order-no:hover {
  text-decoration: underline;
}

.copy-icon {
  color: #4461F2;
  flex-shrink: 0;
}

.amount {
  font-size: 20px;
  color: #0369A1;
  font-weight: 700;
}

.amount-pay {
  font-size: 24px;
  color: #DC2626;
  font-weight: 700;
}

/* 操作区域 */
.action-section {
  text-align: center;
  margin-top: 24px;
}

.btn {
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary {
  background: linear-gradient(135deg, #4461F2 0%, #38BFFF 100%);
  color: white;
  padding: 16px 32px;
  box-shadow: 0 4px 14px rgba(68, 97, 242, 0.4);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(68, 97, 242, 0.5);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6B7280;
  color: white;
  padding: 12px 24px;
}

.btn-secondary:hover {
  background: #4B5563;
}

.btn-large {
  width: 100%;
  font-size: 18px;
  padding: 18px;
}

.btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.action-tip {
  margin-top: 12px;
  font-size: 12px;
  color: #9CA3AF;
}

/* 客服联系 */
.contact-section {
  background: #F3F4F6;
  padding: 16px 24px;
  text-align: center;
  border-top: 1px solid #E5E7EB;
}

.contact-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0;
  font-size: 13px;
  color: #6B7280;
}

.contact-text svg {
  color: #9CA3AF;
  flex-shrink: 0;
}

/* 移动端适配 */
@media (max-width: 640px) {
  .pay-verify-container {
    padding: 12px;
  }

  .verify-card {
    border-radius: 16px;
  }

  .anti-fraud-notice {
    margin: 16px;
    padding: 16px;
  }

  .notice-list li {
    font-size: 13px;
  }

  .order-info {
    padding: 0 16px 16px;
  }

  .info-section {
    padding: 16px;
  }

  .info-row.highlight {
    padding: 12px;
  }

  .amount {
    font-size: 18px;
  }

  .amount-pay {
    font-size: 20px;
  }

  .btn-large {
    font-size: 16px;
    padding: 16px;
  }
}
</style>
